export type WireFilterOp = 'eq' | 'gte' | 'lte' | 'like' | 'in'

export interface TableFilter {
  id: string
  value: unknown
}

export interface FilterWireSpec {
  /** Chiave wire concordata col backend (whitelist PROCESS_FILTERABLE_FIELDS) */
  key: string
  /** Operatore per valori scalari; assente → formato legacy key=value */
  op?: WireFilterOp
  /** Coppia di operatori [min, max] per valori range */
  rangeOps?: [WireFilterOp, WireFilterOp]
}

const isEmpty = (value: unknown): boolean => value === undefined || value === null || value === ''

// Il `;` separa le coppie `campo:op=valore` nella grammatica wire e non esiste
// un meccanismo di escaping: un valore che lo contiene verrebbe spezzato in due
// coppie dal parser del gateway, corrompendo la query. Lo rimuoviamo alla fonte.
const sanitize = (value: unknown): string => String(value).replace(/;/g, '')

/**
 * Serializza i filtri di TanStack Table nel formato wire del gateway:
 * coppie `campo:op=valore` separate da `;` (grammatica di query.helper.ts).
 * Nessun encodeURIComponent: axios encoda già i query param (il doppio
 * encode rompeva il cast delle date lato gateway).
 *
 * Con `specs`: la colonna è tradotta nella chiave wire concordata; le colonne
 * senza spec vengono scartate (gli accessorKey tipo `process.department.name`
 * come chiave SQL darebbero alias inesistenti → 500).
 */
export const serializeTableFilters = (
  filters: TableFilter[],
  specs?: Record<string, FilterWireSpec>
): string => {
  const parts: string[] = []

  for (const filter of filters ?? []) {
    const spec = specs?.[filter.id]
    if (specs && !spec) continue

    const key = spec?.key ?? filter.id

    if (Array.isArray(filter.value) && spec?.rangeOps) {
      const [minOp, maxOp] = spec.rangeOps
      const [min, max] = filter.value as [unknown, unknown]
      if (!isEmpty(min)) {
        const minValue = sanitize(min)
        if (minValue !== '') parts.push(`${key}:${minOp}=${minValue}`)
      }
      if (!isEmpty(max)) {
        const maxValue = sanitize(max)
        if (maxValue !== '') parts.push(`${key}:${maxOp}=${maxValue}`)
      }
      continue
    }

    if (Array.isArray(filter.value)) {
      const values = (filter.value as unknown[])
        .filter(item => !isEmpty(item))
        .map(sanitize)
        .filter(item => item !== '')
      if (values.length === 0) continue
      // Senza `specs` non esiste una chiave wire concordata: le tabelle server-side
      // storiche (utenti, dipartimenti, corsi, ...) si aspettano ancora la
      // coercizione legacy dell'array a stringa, senza operatore — il path legacy
      // del gateway scarta le chiavi che portano un operatore (`:in`).
      if (!specs) {
        parts.push(`${key}=${values.join(',')}`)
        continue
      }
      parts.push(`${key}:in=${values.join(',')}`)
      continue
    }

    if (isEmpty(filter.value)) continue
    const value = sanitize(filter.value)
    if (value === '') continue
    parts.push(spec?.op ? `${key}:${spec.op}=${value}` : `${key}=${value}`)
  }

  return parts.join(';')
}
